import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';

Font.register({
  family: 'Poppins',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/poppins/v21/pxiEyp8kv8JHgFVrFJA.ttf', fontWeight: 400 },
    { src: 'https://fonts.gstatic.com/s/poppins/v21/pxiByp8kv8JHgFVrLCz7V1s.ttf', fontWeight: 700 },
  ],
});

const SZ = [0, 7, 9, 12, 17];

const s = StyleSheet.create({
  page: { padding: 0, backgroundColor: '#fff' },
  label: {
    position: 'absolute',
    top: 50,
    left: 50,
    width: 310,
    height: 360,
    border: '0.8pt solid #111',
    backgroundColor: '#fff',
    fontFamily: 'Poppins',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  row: { flexDirection: 'row' },
  bb: { borderBottom: '0.8pt solid #111' },

  leftCell: { width: 120, alignItems: 'center', justifyContent: 'center', borderRight: '0.8pt solid #111' },
  leftCellLogo: { padding: 8, alignItems: 'center' },
  logo: { width: 90, marginBottom: 0 },
  remitenteCell: { flex: 1, padding: '10 14', justifyContent: 'center', lineHeight: 1, overflow: 'hidden' },
  referenciaCell: { flex: 1, padding: '4 14 10 14', justifyContent: 'center', lineHeight: 1, overflow: 'hidden' },
  refSectionLabel: { fontSize: SZ[1], color: '#000', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 },
  sectionLabel:  { fontSize: SZ[1], color: '#000', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 },

  // Tamaño 3 — nombre empresa / nombre cliente
  fromName: { fontSize: SZ[3], fontWeight: 700, color: '#000', marginBottom: 3 },

  // Tamaño 2 — datos remitente (sucursal, dirección, CUIT)
  fromValue:     { fontSize: SZ[2], color: '#000', marginBottom: 2.5 },
  fromValueBold: { fontSize: SZ[2], fontWeight: 700, color: '#000', marginBottom: 2.5 },

  toBlock: { flex: 1, padding: '8 14 10 14', overflow: 'hidden' },
  divider: { borderBottom: '0.8pt solid #111' },

  // Tamaño 4 — dirección cliente (hasta 2 líneas, corta con … si excede)
  toAddress: { fontSize: SZ[4], fontWeight: 700, color: '#000', marginBottom: 0, maxLines: 2, textOverflow: 'ellipsis' },

  // Tamaño 2 — localidad (separación antes del bloque destinatario)
  toUbicacion: { fontSize: SZ[2], color: '#000', marginBottom: 5 },

  // Tamaño 3 — nombre cliente
  toName: { fontSize: SZ[3], fontWeight: 700, color: '#000', marginBottom: 1 },

  // Tamaño 2 — email / teléfono
  toMeta: { fontSize: SZ[2], color: '#000', marginBottom: 1 },

  footer: { padding: '4 14', borderTop: '0.8pt solid #111', textAlign: 'center' },
  footerText: { fontSize: SZ[1], color: '#000' },
});

const clip = (str, maxChars) =>
  str && str.length > maxChars ? str.slice(0, maxChars) + '…' : str;


const EtiquetaEmpaqueDocument = ({ data, logoUrl, observacionesEnvio }) => {
  // Pueden bajar de línea
  const usaLugarEntrega = data?.usa_lugar_entrega === 'S';
  const destinatarioNombre = [data.destinatario_nombre, data.destinatario_apellido].filter(Boolean).join(' ');
  const destinatarioDireccion = usaLugarEntrega
    ? data?.lugar_entrega || ''
    : [
        data?.destinatario_domicilio,
        data?.destinatario_cod_postal ? `CP ${data?.destinatario_cod_postal}` : null,
      ].filter(Boolean).join(' - ');

  // Se truncan si son muy largos
  const destinatarioUbicacion = usaLugarEntrega
    ? ''
    : clip([
        data?.destinatario_barrio,
        data?.destinatario_localidad,
        data?.destinatario_provincia,
      ].filter(Boolean).join(' - '), 50);

  return (
    <Document>
      <Page size="A4" orientation="portrait" style={s.page}>
        <View style={s.label}>

          {/* Fila 1: Logo | ENVIA + REFERENCIA */}
          <View style={[s.row, s.bb, { overflow: 'hidden', height: 120 }]}> 
            <View style={s.leftCell}>
              <View style={s.leftCellLogo}>
                {logoUrl ? <Image src={logoUrl} style={s.logo} /> : null}
              </View>
            </View>
            <View style={{ flex: 1, flexDirection: 'column' }}>
              <View style={[s.remitenteCell, s.bb, { flex: 3 }]}>
                <Text style={s.sectionLabel}>ENVIA</Text>
                <Text style={s.fromName}>{clip(data.remitente_nombre || '', 28)}</Text>
                {data.remitente_sucursal ? <Text style={s.fromValue}>{clip(data.remitente_sucursal, 32)}</Text> : null}
                {data.remitente_direccion ? <Text style={s.fromValue}>{clip(data.remitente_direccion, 32)}</Text> : null}
                {data.remitente_cuit ? <Text style={s.fromValue}>CUIT: {data.remitente_cuit}</Text> : null}
              </View>
              <View style={[s.referenciaCell, { flex: 1 }]}>
                <Text style={s.refSectionLabel}>REFERENCIA</Text>
                <Text style={s.fromValue}>{clip(data.referencia_tipo || '', 10)} <Text style={s.fromValueBold}>{clip(data.referencia_nro || '---', 35)}</Text></Text>
              </View>
            </View>
          </View>

          {/* Fila 2: Dirección */}
          <View style={[s.toBlock, s.divider, { flexGrow: 1, flexShrink: 0, flexBasis: 'auto' }]}>
            <Text style={s.sectionLabel}>DIRECCION</Text>
            {destinatarioDireccion ? <Text style={s.toAddress}>{destinatarioDireccion}</Text> : null}
            {destinatarioUbicacion ? <Text style={s.toUbicacion}>{destinatarioUbicacion}</Text> : null}
          </View>

          {/* Fila 3: Destinatario | Observaciones */}
          <View style={[s.row, { flexGrow: 2, flexShrink: 1, flexBasis: 'auto', overflow: 'hidden' }]}>
            <View style={{ flex: 1, padding: '8 14 10 14' }}>
              <Text style={s.sectionLabel}>DESTINATARIO</Text>
              {destinatarioNombre ? (
                <Text style={s.toName}>
                  {clip(`${destinatarioNombre}${data.destinatario_nro_doc ? ` (${data.destinatario_nro_doc})` : ''}`, 70)}
                </Text>
              ) : null}
              {(data.destinatario_telefono || data.destinatario_cel) ? (
                <Text style={s.toMeta}>{clip(`Tel: ${data.destinatario_telefono || data.destinatario_cel}`, 45)}</Text>
              ) : null}
              {data.destinatario_email ? <Text style={s.toMeta}>{clip(`Email: ${data.destinatario_email}`, 45)}</Text> : null}
            </View>
            {observacionesEnvio ? (
              <View style={{ flex: 1, padding: '8 14 10 14', borderLeft: '0.8pt solid #111' }}>
                <Text style={s.sectionLabel}>OBSERVACIONES</Text>
                <Text style={s.toMeta}>{clip(observacionesEnvio, 100)}</Text>
              </View>
            ) : null}
          </View>

          {/* Footer */}
          <View style={s.footer}>
            <Text style={s.footerText}>Generado por www.duxsoftware.com.ar</Text>
          </View>

        </View>
      </Page>
    </Document>
  );
};

export default EtiquetaEmpaqueDocument;
